import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, InputBase, IconButton, CircularProgress, Divider, Avatar } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ShowCard from './ShowCard';
import useStore from '../store';
import { searchShows } from '../services/showClient';

function SearchResultItem({ result, alreadyRated, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 1,
        borderRadius: 1.5,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
      }}
    >
      <Avatar
        src={result.poster_path || undefined}
        variant="rounded"
        sx={{ width: 36, height: 54, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.06)', flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {result.title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {result.year || '—'}
        </Typography>
      </Box>
      {alreadyRated && (
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, flexShrink: 0, fontSize: '0.65rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Rated
        </Typography>
      )}
    </Box>
  );
}

export default function TrackerScreen({ onRateShow }) {
  const shows = useStore(s => s.shows);
  const userRatings = useStore(s => s.userRatings);

  const ratedShowsFromStore = shows
    .filter(s => (userRatings[String(s.id)]?.rating ?? 0) > 0)
    .map(s => ({ ...s, ...userRatings[String(s.id)] }));

  const aiRatedShows = Object.values(userRatings)
    .filter(r => {
      const isRated = (r.rating ?? 0) > 0;
      const isNotInBaseById = !shows.some(s => String(s.id) === String(r.showId));
      const isNotInBaseByTitle = !shows.some(s => (s.title || '').toLowerCase().trim() === (r.title || '').toLowerCase().trim());
      return isRated && isNotInBaseById && isNotInBaseByTitle;
    })
    .map(r => ({ id: r.showId, ...r }));

  const ratedShows = [...ratedShowsFromStore, ...aiRatedShows]
    .sort((a, b) => b.rating - a.rating);

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const results = await searchShows(query.trim());
      setSearchResults(results);
      setSearching(false);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const ratedIds = new Set(Object.keys(userRatings));
  const ratedTitles = new Set(
    Object.values(userRatings).map(r => (r.title || '').toLowerCase().trim())
  );

  const isAlreadyRated = (result) =>
    ratedIds.has(result.id) || ratedTitles.has((result.title || '').toLowerCase().trim());

  return (
    <Box sx={{ p: 2 }}>
      {/* Search bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          mb: 1.5,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {searching
          ? <CircularProgress size={16} sx={{ color: 'primary.main', flexShrink: 0 }} />
          : <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
        }
        <InputBase
          placeholder="Search to add a show..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          fullWidth
          sx={{ fontSize: '0.875rem', color: 'text.primary' }}
          inputProps={{ 'aria-label': 'search shows' }}
        />
        {query && (
          <IconButton size="small" onClick={() => setQuery('')} sx={{ p: 0.25 }}>
            <ClearIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </IconButton>
        )}
      </Box>

      {/* Search results */}
      {searchResults.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {searchResults.map(result => (
            <SearchResultItem
              key={result.id}
              result={result}
              alreadyRated={isAlreadyRated(result)}
              onClick={() => onRateShow(result)}
            />
          ))}
          <Divider sx={{ mt: 1.5, borderColor: 'rgba(255,255,255,0.07)' }} />
        </Box>
      )}

      {/* Rated shows count */}
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
        {ratedShows.length} show{ratedShows.length !== 1 ? 's' : ''} rated
      </Typography>

      {ratedShows.length === 0 ? (
        <Typography sx={{ color: 'text.secondary', textAlign: 'center', mt: 8 }}>
          No rated shows yet — search above or tap a show in Watch List to get started.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {ratedShows.map(show => (
            <ShowCard key={show.id} show={show} onClick={() => onRateShow(show)} />
          ))}
        </Box>
      )}
    </Box>
  );
}
